import React, { useState, useEffect, useCallback, useRef } from 'react'
import { apiGet, apiPost, apiUploadFile } from '../../utils/api'
import { WABrowseTab } from './WABrowseTab'
import { WhatsAppUploadWizard } from './WhatsAppUploadWizard'

export interface WhatsAppChat {
  id: number
  chat_number: number
  group_name: string
  display_name: string
  participant_count: number
  message_count: number
  media_count: number
  first_message_date: string | null
  last_message_date: string | null
  first_message: string | null
  zip_filename: string | null
  zip_filesize: number
  status: string
  created_at: string
}

export interface WhatsAppStats {
  total_chats: number
  total_messages: number
  total_media: number
  total_participants: number
  linked_chats: number
  unlinked_chats: number
}

type TabId = 'browse' | 'upload'

export const WhatsAppArchiveView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('browse')
  const [stats, setStats] = useState<WhatsAppStats | null>(null)
  const [isScanning, setIsScanning] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [scanResult, setScanResult] = useState<string | null>(null)
  const [uploadResult, setUploadResult] = useState<string | null>(null)
  const [showUploadWizard, setShowUploadWizard] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiGet<WhatsAppStats>('/whatsapp-archive/stats')
      setStats(data)
    } catch (err) {
      console.error('Failed to fetch WhatsApp stats:', err)
    }
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const handleScan = async () => {
    setIsScanning(true)
    setScanResult(null)
    try {
      const result = await apiPost<{
        message: string
        scanned: number
        imported: number
        skipped: number
        errors: number
        errorDetails?: string[]
      }>('/whatsapp-archive/scan')
      setScanResult(`Imported ${result.imported} new chats (${result.skipped} skipped, ${result.errors} errors)`)
      if (result.imported > 0) fetchStats()
    } catch (err) {
      setScanResult(`Scan failed: ${(err as Error).message}`)
    } finally {
      setIsScanning(false)
    }
  }

  const handleUploadClick = () => {
    setShowUploadWizard(true)
  }

  const handleWizardImportComplete = () => {
    fetchStats()
    setUploadResult('Chat imported successfully')
  }

  // Auto-clear results after 8 seconds
  useEffect(() => {
    if (scanResult) {
      const t = setTimeout(() => setScanResult(null), 8000)
      return () => clearTimeout(t)
    }
  }, [scanResult])

  useEffect(() => {
    if (uploadResult) {
      const t = setTimeout(() => setUploadResult(null), 8000)
      return () => clearTimeout(t)
    }
  }, [uploadResult])

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-border bg-surface-primary">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-txt-primary dark:text-white flex items-center gap-2">
              {/* WhatsApp phone icon in brand green */}
              <svg
                className="w-6 h-6"
                style={{ color: '#25D366' }}
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M17.465 21.25c2.354-1.277 4.084-3.5 4.514-6.082.44-2.582.06-5.247-1.12-7.605C19.74 3.857 17.5 1.55 14.67.51 11.84-.527 8.64-.157 6.19 1.12 3.74 2.4 1.93 4.63 1.5 7.21c-.44 2.58-.06 5.25 1.12 7.61 1.18 2.36 3.42 4.67 6.25 5.71 2.83 1.04 6.03.67 8.48-.61l.115.063zm-7.464-3.91c-2.184 0-3.965-1.78-3.965-3.965 0-2.185 1.78-3.965 3.965-3.965 2.184 0 3.965 1.78 3.965 3.965 0 2.185-1.78 3.965-3.965 3.965z" />
              </svg>
              WhatsApp Archive
            </h1>
            <p className="text-sm text-txt-secondary mt-0.5">
              Browse and search {stats?.total_chats?.toLocaleString() || '...'} archived WhatsApp group conversations
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Status messages */}
            {(scanResult || uploadResult) && (
              <span className={`text-xs px-3 py-1.5 rounded-lg ${
                (scanResult || uploadResult || '').startsWith('Error') || (scanResult || uploadResult || '').includes('failed')
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-green-50 text-green-700 border border-green-200'
              }`}>
                {scanResult || uploadResult}
              </span>
            )}

            {/* Upload single ZIP button — opens wizard */}
            <button
              onClick={handleUploadClick}
              disabled={isUploading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-border bg-white text-txt-primary hover:bg-surface-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Importing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                  </svg>
                  Upload ZIP
                </>
              )}
            </button>

            {/* Scan & import button */}
            <button
              onClick={handleScan}
              disabled={isScanning}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isScanning ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Scanning...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
                  </svg>
                  Scan Directory
                </>
              )}
            </button>
          </div>
        </div>

        {/* Stats row — 6 columns across */}
        <div className="mt-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.75rem' }}>
          {/* Total Chats */}
          <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-xl font-bold text-emerald-700 leading-tight">
                {stats?.total_chats?.toLocaleString() || '—'}
              </div>
              <div className="text-[11px] text-emerald-600/70 font-medium">Chats</div>
            </div>
          </div>

          {/* Total Messages */}
          <div className="rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-xl font-bold text-blue-700 leading-tight">
                {stats?.total_messages?.toLocaleString() || '—'}
              </div>
              <div className="text-[11px] text-blue-600/70 font-medium">Messages</div>
            </div>
          </div>

          {/* Total Media */}
          <div className="rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-xl font-bold text-violet-700 leading-tight">
                {stats?.total_media?.toLocaleString() || '—'}
              </div>
              <div className="text-[11px] text-violet-600/70 font-medium">Media</div>
            </div>
          </div>

          {/* Total Participants */}
          <div className="rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-xl font-bold text-amber-700 leading-tight">
                {stats?.total_participants?.toLocaleString() || '—'}
              </div>
              <div className="text-[11px] text-amber-600/70 font-medium">Participants</div>
            </div>
          </div>

          {/* Linked to Customers */}
          <div className="rounded-xl bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-teal-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.052a4.5 4.5 0 00-6.364-6.364L4.757 8.257a4.5 4.5 0 006.364 6.364l4.5-4.5z" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-xl font-bold text-teal-700 leading-tight">
                {stats?.linked_chats?.toLocaleString() || '—'}
              </div>
              <div className="text-[11px] text-teal-600/70 font-medium">Linked</div>
            </div>
          </div>

          {/* Unlinked */}
          <div className="rounded-xl bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200/60 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-rose-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.181 8.68a4.503 4.503 0 011.903 6.405m-9.768-2.782L3.56 14.06a4.5 4.5 0 006.364 6.365l3.129-3.129m5.614-5.615l1.757-1.757a4.5 4.5 0 00-6.364-6.365l-3.129 3.129m-2.95 7.827l7.071-7.071" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-xl font-bold text-rose-700 leading-tight">
                {stats?.unlinked_chats?.toLocaleString() || '—'}
              </div>
              <div className="text-[11px] text-rose-600/70 font-medium">Unlinked</div>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mt-4">
          <button
            onClick={() => setActiveTab('browse')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'browse'
                ? 'bg-brand-600 text-white'
                : 'text-txt-secondary hover:bg-surface-hover'
            }`}
          >
            Browse
          </button>
          <button
            onClick={() => setActiveTab('upload')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'upload'
                ? 'bg-brand-600 text-white'
                : 'text-txt-secondary hover:bg-surface-hover'
            }`}
          >
            Upload
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'browse' && <WABrowseTab stats={stats} />}
        {activeTab === 'upload' && (
          <div className="h-full overflow-auto">
            <div className="p-6">
              <div className="max-w-2xl mx-auto">
                <div className="border-2 border-dashed border-border rounded-xl p-10 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-txt-secondary mb-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33A3 3 0 0116.5 19.5H6.75z"
                    />
                  </svg>
                  <h3 className="text-lg font-semibold text-txt-primary mb-2">
                    Upload WhatsApp Export
                  </h3>
                  <p className="text-sm text-txt-secondary mb-4">
                    Upload a ZIP file and preview the contents before importing to the database.
                  </p>
                  <button
                    onClick={handleUploadClick}
                    className="inline-flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                    </svg>
                    Start Import Wizard
                  </button>
                </div>

                <div className="mt-8 space-y-4">
                  <h4 className="text-sm font-semibold text-txt-primary">How to export from WhatsApp</h4>
                  <ol className="text-sm text-txt-secondary space-y-2 list-decimal list-inside">
                    <li>Open WhatsApp and go to a chat or group</li>
                    <li>Tap the menu icon and select "More"</li>
                    <li>Choose "Export chat" and select "Without media" or "With media"</li>
                    <li>Save the export as a ZIP file</li>
                    <li>Upload the ZIP file here to preview and import</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Upload Wizard Modal */}
      <WhatsAppUploadWizard
        isOpen={showUploadWizard}
        onClose={() => setShowUploadWizard(false)}
        onImportComplete={handleWizardImportComplete}
      />
    </div>
  )
}
